import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getSignedUrl } from 'legacy/src/shared/clients/apiClient';
import * as types from 'legacy/src/constants/types';
import {
  PDFS,
  APCLASSROOM_FOLDER,
  teacherGuidesKey,
  studentGuidesKey,
  apKey,
  defaultKey,
  preApKey,
  baseClickHelp,
} from './FAQHelper';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import Icon from 'chameleon/ui-stack/ui/base/icon';

const FAQ = ({ isPreAPCoord, userData }) => {
  const {
    isStudent,
    isTeacher,
    isAdmin,
    isCoordinator,
    teacherSubjects,
    studentSubjects,
    adminSubjects,
  } = userData;

  const isPreAPTeacher = teacherSubjects.some(
    (subject) => subject.subjectProgram === types.PreAP,
  );
  const isAPTeacher = teacherSubjects.some(
    (subject) => subject.subjectProgram === types.AP,
  );
  const isPreAPStudent = studentSubjects.some(
    (subject) => subject.subjectProgram === types.PreAP,
  );
  const isAPStudent = studentSubjects.some(
    (subject) => subject.subjectProgram === types.AP,
  );
  const isAPAdmin = adminSubjects.some(
    (subject) => subject.subjectProgram === types.AP,
  );
  const isPreAPAdmin = adminSubjects.some(
    (subject) => subject.subjectProgram === types.PreAP,
  );
  const faqLink = `${baseClickHelp}/#!ap-classroom-faqs-publication`;

  /**
   * Store Pdf Items
   * @param {array} toArray The array where we store the new items.
   * @param {array} someArguments A list of arguments
   */
  const storePdfItems = (toArray, ...someArguments) => {
    const items = getPdfItems(...someArguments);
    if (items) {
      toArray.push(items);
    }
  };

  /**
   * Get pdf items list
   * @param {object} PDFS The pdf configuration object
   * @param {string} section First level - section [teacherGuides - studentGuides]
   * @param {string} ENV Second level - ENV [infra - behavioral]
   * @param {array} levelArguments The list of arguments sent by caller
   * @return {object} Path (to list of items) and List of items or NULL
   * */
  const getPdfItems = (PDFS, section, ENV, ...levelArguments) => {
    let itemsFound = true;
    const sectionPath = PDFS[section];
    const envPath = (sectionPath && sectionPath[ENV]) || null;
    let returnObj = null;
    let path;

    if (envPath) {
      path = envPath.path;
      let PDFSbyPath = envPath;
      levelArguments.forEach((level) => {
        if (PDFSbyPath[level]) {
          PDFSbyPath = PDFSbyPath[level];
        } else {
          itemsFound = false;
        }
      });

      if (itemsFound) {
        returnObj = {
          items: PDFSbyPath,
          path,
        };
      }
    }

    return returnObj;
  };

  const PDF_ENV = () => {
    if (isAPAdmin || isAPTeacher || isAPStudent) {
      return apKey;
    } else if (
      isPreAPTeacher ||
      isPreAPStudent ||
      isPreAPCoord ||
      isPreAPAdmin
    ) {
      return preApKey;
    }
    return 'behavioral';
  };

  /**
   * Renders list of pdfs
   * @param {array|object} pdfItems List of items
   * @return React element
   * */
  const pdfRender = (pdfItems) => {
    const pilot = APCLASSROOM_FOLDER;
    const { path } = pdfItems;
    const baseDir = path ? `${pilot}${path}` : `${pilot}/`;

    return pdfItems.items.map((l, i) => {
      if (l.webLink) {
        return (
          <li key={i} className="list-none pt-8.1 pb-4.8 pl-16.6 pr-8.5">
            <A11y>
              <a
                className="flex justify-between items-center text-blue-800 text-16 font-normal"
                tabIndex={0}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {l.title}
                <Icon id="external_link" className="text-gray-700" />
              </a>
            </A11y>
          </li>
        );
      }

      let linkRef = React.createRef();
      let key = `${baseDir}${l.href}`;
      getSignedUrl({ key, bucket: 'help.media.academicmerit.com' }).then(
        (signedURL) => {
          if (linkRef.current) {
            linkRef.current.href = signedURL.replace(
              /\/[^\/]+\//,
              '/media.academicmerit.com/',
            );
            linkRef.current.target = '_blank';
          }
        },
      );
      return (
        <li key={i} className="list-none pt-8.1 pb-4.8 pl-16.6 pr-8.5">
          <a
            className="grid grid-flow-col justify-between items-center"
            ref={linkRef}
            href="#"
          >
            {l.title}
            <Icon id="external_link" className="text-gray-700" />
          </a>
        </li>
      );
    });
  };

  let teacherGuides = [];
  let studentGuides = [];

  let userType;
  if (isAdmin) {
    userType = 'admin';
  } else if (isCoordinator) {
    userType = 'coordinator';
  } else if (isTeacher) {
    userType = 'teacher';
  } else if (isStudent) {
    userType = 'student';
  }

  if (isPreAPCoord) {
    storePdfItems(teacherGuides, PDFS, teacherGuidesKey, preApKey, userType);
    if (isAPAdmin) {
      storePdfItems(teacherGuides, PDFS, teacherGuidesKey, apKey, userType);
      storePdfItems(teacherGuides, PDFS, teacherGuidesKey, apKey, defaultKey);
      storePdfItems(studentGuides, PDFS, studentGuidesKey, apKey, defaultKey);
    }
  } else if (isCoordinator || isAdmin || isTeacher) {
    storePdfItems(teacherGuides, PDFS, teacherGuidesKey, PDF_ENV(), userType);
    storePdfItems(teacherGuides, PDFS, teacherGuidesKey, PDF_ENV(), defaultKey);
    storePdfItems(studentGuides, PDFS, studentGuidesKey, PDF_ENV(), defaultKey);
  }

  if (isStudent) {
    storePdfItems(studentGuides, PDFS, studentGuidesKey, PDF_ENV(), defaultKey);
  }

  return (
    <div>
      <h4 className="flex items-center m-0 bg-gray-100 pl-8 pt-4.2 pb-0.9 border-b border-gray-300 uppercase text-black text-14 font-medium">
        step-by-step Instructions and Videos
      </h4>
      <ul className="list-none p-0 m-0">
        {(isAPTeacher || isAPAdmin || isPreAPCoord) &&
          (teacherGuides || []).map((item) => pdfRender(item))}
        {(studentGuides || []).map((item) => pdfRender(item))}
        {(isAPAdmin || isAPTeacher) && (
          <li className="list-none pt-8.1 pb-4.8 pl-16.6 pr-8.5">
            <A11y>
              <a
                className="flex justify-between items-center text-blue-800 text-16 font-normal"
                tabIndex={0}
                href={faqLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
                <Icon id="external_link" className="text-gray-700" />
              </a>
            </A11y>
          </li>
        )}
      </ul>
    </div>
  );
};

FAQ.displayName = 'FAQ';

FAQ.propTypes = {
  userData: PropTypes.object,
  location: PropTypes.object,
  regularSubjects: PropTypes.array,
  adminSubjects: PropTypes.array,
};

function mapStateToProps(state) {
  const { userData } = state;

  return {
    userData,
    regularSubjects: userData.studentSubjects.concat(userData.teacherSubjects),
    adminSubjects: userData.adminSubjects,
    subjectData: state.subject,
  };
}

export default connect(mapStateToProps)(FAQ);
